import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import OutsideClickHandler from 'react-outside-click-handler';
import Modal from 'react-responsive-modal';
import { Link, useHistory } from 'react-router-dom';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import api from '../../services/api';
import refreshToken from '../../utils/refreshToken';
import { PERM } from '../../utils/permissions';
import ModalPropertiesPerConsultant from '../ModalPropertiesPerConsultant';
import ModalPropertiesPerNeighborhood from '../ModalPropertiesPerNeighborhood';
import ModalVisitsPerConsultants from '../ModalVisitsPerConsultants';
import ModalVisitsPerOwner from '../ModalVisitsPerOwner';
import ModalVisitsPerProperty from '../ModalVisitsPerProperty';
import ModalActivePropertiesPerPeriod from './../ModalActivePropertiesPerPeriod';
import Logo7Cantos from './../../assets/components/Header/logoTopo.svg';

import {
    Content,
    RoutesOptions,
    Separator,
    OptionsToRouteContainer,
    RouteOption
} from './styles';

interface IMenuToNavigate {
    open: boolean;
    functionToCloseModal(): void;
}

interface IBoard {
    id: number;
    name: string;
}

export default function MenuToNavigate({ open, functionToCloseModal }: IMenuToNavigate) {
    const { user, dateToExpires, access_token, signOut, hasPermission, hasAnyPermission } = useAuth();
    const { addToast } = useToast();

    const history = useHistory();

    const [openOptionsToRouteConsultants, setOpenOptionsToRouteConsultants] = useState(false);
    const [openOptionsToRouteProperties, setOpenOptionsToRouteProperties] = useState(false);
    const [openOptionsToReports, setOpenOptionsToReports] = useState(false);
    const [openOptionsToFAQ, setOpenOptionsToFAQ] = useState(false);
    const [openOptionsToRent, setOpenOptionsToRent] = useState(false);
    const [openOptionsToBoards, setOpenOptionsToBoards] = useState(false);
    const [openOptionsToMorePartner, setOpenOptionsToMorePartner] = useState(false);
    const [openOptionsToPartnerUsers, setOpenOptionsToPartnerUsers] = useState(false);
    const [openOptionsToMoreGeneral, setOpenOptionsToMoreGeneral] = useState(false);

    const [openModalPropertiesPerConsultant, setOpenModalPropertiesPerConsultant] = useState(false);
    const [openModalPropertiesPerNeighborhood, setOpenModalPropertiesPerNeighborhood] = useState(false);
    const [openModalVisitsPerConsultant, setOpenModalVisitsPerConsultant] = useState(false);
    const [openModalVisitsPerOwner, setOpenModalVisitsPerOwner] = useState(false);
    const [openModalVisitsPerProperty, setOpenModalVisitsPerProperty] = useState(false);
    const [openModalActivePropertiesPerPeriod, setOpenModalActivePropertiesPerPeriod] = useState(false);

    const [offers, setOffers] = useState<any[]>([]);
    const [boards, setBoards] = useState<IBoard[]>([]);
    const [loadingBoards, setLoadingBoards] = useState(false);

    const isPhotographer = user?.role === 'photographer';
    const isPartnerBroker = user?.role === 'partner_broker';
    const canLoadUnreadOffers =
        hasPermission(PERM.offerUnread) ||
        (isPartnerBroker && hasPermission(PERM.offerList));
    const canAccessBoards = hasAnyPermission(['adm.boards.index', 'adm.boards.show']);
    const canImoveisBlock = hasAnyPermission([PERM.propertyList, PERM.propertyCreate]);
    const canReports = hasAnyPermission([
        PERM.reportConsultant,
        PERM.reportNeighborhood,
        PERM.reportVisits,
        PERM.reportVisitsByOwner,
        PERM.reportVisitsByProperty,
        PERM.reportActives,
    ]);
    const canFAQBlock = hasAnyPermission([PERM.faqList, PERM.faqCreate]);
    const canUsersBlock = hasAnyPermission([PERM.usersList, PERM.usersCreate, PERM.bulkConsultants]);
    const canRentBlock = hasPermission(PERM.offerList) || hasPermission(PERM.documents);
    const canPhotographerAgenda = hasPermission(PERM.photoSchedule) || isPhotographer;
    const canTeams = hasAnyPermission(['adm.teams.index', 'adm.teams.create', 'adm.teams.update', 'adm.teams.delete']);

    useEffect(() => {
        async function loadApi() {
            try {
                if (canLoadUnreadOffers) {
                    const token = await refreshToken(dateToExpires, access_token);

                    api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

                    const response = await api.get('/api/adm/offers/unread/get');

                    setOffers(response.data.data ?? []);
                } else {
                    setOffers([]);
                }
            } catch (err: any) {
                console.log(err);

                if (!!err.response?.data.message) {
                    addToast({
                        title: err.response.data.message,
                        type: 'error'
                    });
                }
            }
        }

        if (!!user) {
            loadApi();
        } else {
            history.push('/')
        }
    }, [user, history, dateToExpires, access_token, canLoadUnreadOffers]);

    useEffect(() => {
        async function loadBoards() {
            if (!open || !canAccessBoards || !user) return;
            try {
                setLoadingBoards(true);
                const token = await refreshToken(dateToExpires, access_token);
                api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
                const response = await api.get('/api/adm/boards');
                setBoards(response.data.data ?? []);
            } catch (err) {
                console.error(err);
                addToast({
                    title: 'Erro ao carregar os boards',
                    type: 'error',
                });
            } finally {
                setLoadingBoards(false);
            }
        }
        loadBoards();
    }, [open, user, canAccessBoards]);

    const handleSignOut = useCallback(async () => {
        await signOut();
        history.push('/');
    }, [signOut, history]);

    return (
        <Modal
            open={open}
            onClose={functionToCloseModal}
            closeIcon={(<X className="h-[18px] w-[18px] text-black" aria-hidden />)}
            classNames={{
                modal: 'menu'
            }}
        >
            <Content>
                <RoutesOptions>
                    <Link to={!isPhotographer ? "/schedules" : '/photographers-schedule'}>
                        <img src={Logo7Cantos} alt="7cantos-logo" />
                    </Link>

                    {canAccessBoards && (
                        <button onClick={() => setOpenOptionsToBoards(true)}>
                            <p>Boards</p>
                            <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                            {openOptionsToBoards && (
                                <OutsideClickHandler onOutsideClick={() => setOpenOptionsToBoards(false)}>
                                    <OptionsToRouteContainer style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                        {loadingBoards ? (
                                            <span>Carregando boards...</span>
                                        ) : (
                                            boards.map((board) => (
                                                <Link
                                                    key={board.id}
                                                    to={`/kanban/${board.id}`}
                                                    onClick={functionToCloseModal}
                                                >
                                                    {board.name}
                                                </Link>
                                            ))
                                        )}
                                    </OptionsToRouteContainer>
                                </OutsideClickHandler>
                            )}
                        </button>
                    )}

                    {canTeams && (
                        <RouteOption>
                            <Link to="/teams" onClick={functionToCloseModal}>Times</Link>
                        </RouteOption>
                    )}

                    {canImoveisBlock && (
                        <button onClick={() => setOpenOptionsToRouteProperties(true)}>
                            <p>Imóveis</p>

                            <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                            {!!openOptionsToRouteProperties && (
                                <OutsideClickHandler onOutsideClick={() => setOpenOptionsToRouteProperties(false)}>
                                    <OptionsToRouteContainer>
                                        {hasPermission(PERM.propertyList) && (
                                            <Link to="/properties/1" onClick={functionToCloseModal}>
                                                Visualizar
                                            </Link>
                                        )}
                                        {hasPermission(PERM.propertyCreate) && (
                                            <Link to="/create-property">Cadastrar</Link>
                                        )}
                                    </OptionsToRouteContainer>
                                </OutsideClickHandler>
                            )}
                        </button>
                    )}

                    {canReports && (
                        <button onClick={() => setOpenOptionsToReports(true)}>
                            <p>Relatórios</p>

                            <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                            {!!openOptionsToReports && (
                                <OutsideClickHandler onOutsideClick={() => setOpenOptionsToReports(false)}>
                                    <OptionsToRouteContainer>
                                        {hasPermission(PERM.reportConsultant) && (
                                            <button onClick={() => setOpenModalPropertiesPerConsultant(true)}>Imóveis por consultor</button>
                                        )}
                                        {hasPermission(PERM.reportNeighborhood) && (
                                            <button onClick={() => setOpenModalPropertiesPerNeighborhood(true)}>Imóveis por bairro</button>
                                        )}
                                        {hasPermission(PERM.reportVisits) && (
                                            <button onClick={() => setOpenModalVisitsPerConsultant(true)}>Visitas por consultor</button>
                                        )}
                                        {hasPermission(PERM.reportVisitsByOwner) && (
                                            <button onClick={() => setOpenModalVisitsPerOwner(true)}>Visitas por Proprietário</button>
                                        )}
                                        {hasPermission(PERM.reportVisitsByProperty) && (
                                            <button onClick={() => setOpenModalVisitsPerProperty(true)}>Visitas por Imóvel</button>
                                        )}
                                        {hasPermission(PERM.reportActives) && (
                                            <button onClick={() => setOpenModalActivePropertiesPerPeriod(true)}>Imóveis Ativos por Período</button>
                                        )}
                                    </OptionsToRouteContainer>
                                </OutsideClickHandler>
                            )}
                        </button>
                    )}

                    {canFAQBlock && (
                        <button onClick={() => setOpenOptionsToFAQ(true)}>
                            <p>FAQs</p>

                            <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                            {!!openOptionsToFAQ && (
                                <OutsideClickHandler onOutsideClick={() => setOpenOptionsToFAQ(false)}>
                                    <OptionsToRouteContainer>
                                        {hasPermission(PERM.faqList) && (
                                            <Link to="/list-faqs/1">Visualizar</Link>
                                        )}
                                        {hasPermission(PERM.faqCreate) && (
                                            <Link to="/create-faq">Cadastrar</Link>
                                        )}
                                    </OptionsToRouteContainer>
                                </OutsideClickHandler>
                            )}
                        </button>
                    )}

                    {canRentBlock && (
                        <button onClick={() => setOpenOptionsToRent(true)}>
                            <p>Locação</p>

                            {offers.length > 0 && (
                                <span className="notification-dot" />
                            )}

                            <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                            {!!openOptionsToRent && (
                                <OutsideClickHandler onOutsideClick={() => setOpenOptionsToRent(false)}>
                                    <OptionsToRouteContainer>
                                        {hasPermission(PERM.offerList) && (
                                            <Link
                                                to="/offers/1"
                                                className="dropdown-item"
                                                onClick={functionToCloseModal}
                                            >
                                                <span className="label">Propostas</span>

                                                {offers.length > 0 && (
                                                    <span className="offers-count">{offers.length}</span>
                                                )}
                                            </Link>
                                        )}

                                        {hasPermission(PERM.documents) && (
                                            <Link to="/documents" onClick={functionToCloseModal}>
                                                Documentação
                                            </Link>
                                        )}
                                    </OptionsToRouteContainer>
                                </OutsideClickHandler>
                            )}
                        </button>
                    )}

                    {canPhotographerAgenda && !isPartnerBroker && (
                        <RouteOption>
                            <Link to="/photographers-schedule" onClick={functionToCloseModal}>
                                Agenda Fotógrafos
                            </Link>
                        </RouteOption>
                    )}

                    {!isPartnerBroker && (
                        <RouteOption>
                            <Link to="/analise-restritiva" onClick={functionToCloseModal}>
                                Análise Restritiva
                            </Link>
                        </RouteOption>
                    )}

                    {!isPartnerBroker &&
                        hasAnyPermission([
                            PERM.styleGuide,
                            PERM.notificationsLogs,
                            PERM.notificationsAutomations,
                        ]) && (
                        <button type="button" onClick={() => setOpenOptionsToMoreGeneral((v) => !v)}>
                            <p>Mais</p>
                            <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                            {openOptionsToMoreGeneral && (
                                <OutsideClickHandler onOutsideClick={() => setOpenOptionsToMoreGeneral(false)}>
                                    <OptionsToRouteContainer>
                                        {hasAnyPermission([
                                            PERM.notificationsLogs,
                                            PERM.notificationsAutomations,
                                        ]) && (
                                            <Link
                                                to="/notifications/1"
                                                onClick={() => {
                                                    setOpenOptionsToMoreGeneral(false);
                                                    functionToCloseModal();
                                                }}
                                            >
                                                Notificações
                                            </Link>
                                        )}

                                        {hasPermission(PERM.styleGuide) && (
                                            <Link
                                                to="/style-guide"
                                                onClick={() => {
                                                    setOpenOptionsToMoreGeneral(false);
                                                    functionToCloseModal();
                                                }}
                                            >
                                                Guia de estilo
                                            </Link>
                                        )}
                                    </OptionsToRouteContainer>
                                </OutsideClickHandler>
                            )}
                        </button>
                    )}

                    {isPartnerBroker && (
                        <button onClick={() => setOpenOptionsToMorePartner(true)}>
                            <p>Mais</p>
                            <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                            {openOptionsToMorePartner && (
                                <OutsideClickHandler onOutsideClick={() => {
                                    setOpenOptionsToMorePartner(false);
                                    setOpenOptionsToPartnerUsers(false);
                                }}>
                                    <OptionsToRouteContainer>
                                        {hasPermission(PERM.propertyPartner) && (
                                            <Link
                                                to="/my-properties/1"
                                                onClick={() => {
                                                    setOpenOptionsToMorePartner(false);
                                                    functionToCloseModal();
                                                }}
                                            >
                                                Meus imóveis
                                            </Link>
                                        )}

                                        <Link
                                            to="/analise-restritiva"
                                            onClick={() => {
                                                setOpenOptionsToMorePartner(false);
                                                functionToCloseModal();
                                            }}
                                        >
                                            Análise Restritiva
                                        </Link>

                                        {hasPermission(PERM.styleGuide) && (
                                            <Link
                                                to="/style-guide"
                                                onClick={() => {
                                                    setOpenOptionsToMorePartner(false);
                                                    functionToCloseModal();
                                                }}
                                            >
                                                Guia de estilo
                                            </Link>
                                        )}

                                        {hasPermission(PERM.photoSchedule) && (
                                            <Link
                                                to="/photographers-schedule"
                                                onClick={() => {
                                                    setOpenOptionsToMorePartner(false);
                                                    functionToCloseModal();
                                                }}
                                            >
                                                Agenda Fotógrafos
                                            </Link>
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => setOpenOptionsToPartnerUsers((v) => !v)}
                                        >
                                            <p>Usuários</p>
                                            <ChevronDown className="h-4 w-4 text-current" aria-hidden />
                                        </button>

                                        {openOptionsToPartnerUsers && (
                                            <OutsideClickHandler onOutsideClick={() => setOpenOptionsToPartnerUsers(false)}>
                                                <OptionsToRouteContainer>
                                                    <Link
                                                        to="/users"
                                                        onClick={() => {
                                                            setOpenOptionsToMorePartner(false);
                                                            functionToCloseModal();
                                                        }}
                                                    >
                                                        Visualizar
                                                    </Link>
                                                    <Link
                                                        to="/create-user"
                                                        onClick={() => {
                                                            setOpenOptionsToMorePartner(false);
                                                            functionToCloseModal();
                                                        }}
                                                    >
                                                        Cadastrar
                                                    </Link>
                                                    <Link
                                                        to="/consultants/update"
                                                        onClick={() => {
                                                            setOpenOptionsToMorePartner(false);
                                                            functionToCloseModal();
                                                        }}
                                                    >
                                                        Consultores em Massa
                                                    </Link>
                                                </OptionsToRouteContainer>
                                            </OutsideClickHandler>
                                        )}
                                    </OptionsToRouteContainer>
                                </OutsideClickHandler>
                            )}
                        </button>
                    )}

                    {canUsersBlock && (
                        <button onClick={() => setOpenOptionsToRouteConsultants(true)}>
                            <p>Usuários</p>

                            <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                            {!!openOptionsToRouteConsultants && (
                                <OutsideClickHandler onOutsideClick={() => setOpenOptionsToRouteConsultants(false)}>
                                    <OptionsToRouteContainer>
                                        {hasPermission(PERM.usersList) && (
                                            <Link to="/users">Visualizar</Link>
                                        )}
                                        {hasPermission(PERM.usersCreate) && (
                                            <Link to="/create-user">Cadastrar</Link>
                                        )}
                                        {hasPermission(PERM.bulkConsultants) && (
                                            <Link to="/consultants/update">Consultores em Massa</Link>
                                        )}
                                    </OptionsToRouteContainer>
                                </OutsideClickHandler>
                            )}
                        </button>
                    )}
                </RoutesOptions>

                <Separator />

                <RoutesOptions>
                    <h4><Link to='/my-account'>Configurações</Link></h4>

                    <button onClick={handleSignOut}>Sair</button>
                </RoutesOptions>
            </Content>


            {!!openModalPropertiesPerConsultant && (
                <ModalPropertiesPerConsultant open={openModalPropertiesPerConsultant} functionToCloseModal={() => setOpenModalPropertiesPerConsultant(false)} />
            )}

            {!!openModalPropertiesPerNeighborhood && (
                <ModalPropertiesPerNeighborhood open={openModalPropertiesPerNeighborhood} functionToCloseModal={() => setOpenModalPropertiesPerNeighborhood(false)} />
            )}

            {!!openModalVisitsPerConsultant && (
                <ModalVisitsPerConsultants open={openModalVisitsPerConsultant} functionToCloseModal={() => setOpenModalVisitsPerConsultant(false)} />
            )}

            {!!openModalVisitsPerOwner && (
                <ModalVisitsPerOwner open={openModalVisitsPerOwner} functionToCloseModal={() => setOpenModalVisitsPerOwner(false)} />
            )}

            {!!openModalVisitsPerProperty && (
                <ModalVisitsPerProperty open={openModalVisitsPerProperty} functionToCloseModal={() => setOpenModalVisitsPerProperty(false)} />
            )}

            {openModalActivePropertiesPerPeriod && (
                <ModalActivePropertiesPerPeriod open={openModalActivePropertiesPerPeriod} functionToCloseModal={() => setOpenModalActivePropertiesPerPeriod(false)} />
            )}
        </Modal>
    );
}